import React from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
  Button,
} from "lift-ui";
import { ArrowLeft, Eye } from "lucide-react";
import { Markdown } from '../../../../../shared/components/Markdown';

export interface Station {
  buddy_id: string;
  agent_id?: string; // legacy field
  name: string;
  description?: string;
  available_tools?: string[];
  default_model?: string;
  max_tokens?: number;
  temperature?: number | string;
  is_active: boolean;
  meta_config?: Record<string, unknown>;
  policies?: Record<string, unknown>;
  system_prompt?: string;
  version?: number | string;
  created_at?: string;
  updated_at?: string;
  parent_buddy_id?: string | null;
  modified_by?: number | null;
  version_note?: Record<string, any>;
  children?: Station[];
}

interface ViewStationDialogProps {
  open: boolean;
  onClose: () => void;
  station: Station | null;
}

type SheetView = "details" | "prompt";

const ViewStationDialog: React.FC<ViewStationDialogProps> = ({
  open,
  onClose,
  station,
}) => {
  const [view, setView] = React.useState<SheetView>("details");

  const handleClose = () => {
    onClose();
    setView("details");
  };

  const isEmpty = !station?.system_prompt?.trim();

  return (
    <Sheet open={open} onOpenChange={(isOpen) => { if (!isOpen) handleClose(); }}>
      <SheetContent side="right" className="w-full sm:max-w-2xl flex flex-col">
        {view === "details" ? (
          <>
            <SheetHeader>
              <SheetTitle>Station Details</SheetTitle>
            </SheetHeader>

            <div className="flex-1 overflow-y-auto py-6 space-y-4">
              {station && (
                <div className="space-y-4">
                  <div>
                    <strong>Name:</strong> {station.name}
                  </div>
                  <div>
                    <strong>Station ID:</strong> {station.buddy_id}
                  </div>
                  <div>
                    <strong>Status:</strong> {station.is_active ? "Active" : "Inactive"}
                  </div>
                  <div>
                    <strong>Default Model:</strong> {station.default_model}
                  </div>
                  <div>
                    <strong>Version:</strong> {station.version}
                  </div>
                  <div>
                    <strong>Max Tokens:</strong> {station.max_tokens?.toLocaleString()}
                  </div>
                  <div>
                    <strong>Temperature:</strong> {station.temperature}
                  </div>
                  <div>
                    <strong>Available Tools:</strong> {station.available_tools?.length ? station.available_tools.join(', ') : 'None'}
                  </div>

                  {station.meta_config && (
                    <>
                      {station.meta_config.avatar && (
                        <div>
                          <strong>Avatar:</strong> {station.meta_config.avatar as string}
                        </div>
                      )}
                      {station.meta_config.iconSrc && (
                        <div>
                          <strong>Icon:</strong> {station.meta_config.iconSrc as string}
                        </div>
                      )}
                      {station.meta_config.category && (
                        <div>
                          <strong>Category:</strong> {station.meta_config.category as string}
                        </div>
                      )}
                      {station.meta_config.color && (
                        <div>
                          <strong>Color:</strong> {station.meta_config.color as string}
                        </div>
                      )}
                      {station.meta_config.description_brief && (
                        <div>
                          <strong>Brief Description:</strong> {station.meta_config.description_brief as string}
                        </div>
                      )}
                      {station.meta_config.description_main && (
                        <div>
                          <strong>Main Description:</strong> {station.meta_config.description_main as string}
                        </div>
                      )}
                    </>
                  )}

                  {station.policies && (
                    <>
                      {station.policies.tone && (
                        <div>
                          <strong>Tone:</strong> {station.policies.tone as string}
                        </div>
                      )}
                      {station.policies.focus && (
                        <div>
                          <strong>Focus:</strong> {station.policies.focus as string}
                        </div>
                      )}
                      {station.policies.specialties && (station.policies.specialties as string[]).length > 0 && (
                        <div>
                          <strong>Specialties:</strong> {(station.policies.specialties as string[]).join(', ')}
                        </div>
                      )}
                    </>
                  )}
                  <div className="flex items-center gap-2">
                    <strong>System Prompt:</strong>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setView("prompt")}
                      className="h-8 w-8 p-0"
                      title="View full system prompt"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="mt-0.5 bg-gray-100 text-gray-800 p-4 rounded-md max-h-[200px] overflow-auto text-sm [&_p]:mb-1 [&_p]:leading-relaxed [&_ul]:pl-5 [&_ol]:pl-5 [&_ul]:mb-1 [&_ol]:mb-1 [&_li]:mb-1 [&_h1]:mt-6 [&_h2]:mt-6 [&_h3]:mt-6 [&_h4]:mt-6 [&_h1]:mb-3 [&_h2]:mb-3 [&_h3]:mb-3 [&_h4]:mb-3">
                    <Markdown>{station.system_prompt || 'No system prompt'}</Markdown>
                  </div>
                  <div>
                    <strong>Created:</strong> {station.created_at ? new Date(station.created_at).toLocaleString() : 'Unknown'}
                  </div>
                  <div>
                    <strong>Updated:</strong> {station.updated_at ? new Date(station.updated_at).toLocaleString() : 'Unknown'}
                  </div>
                  {station.parent_buddy_id && (
                    <div>
                      <strong>Parent Station:</strong> {station.parent_buddy_id}
                    </div>
                  )}
                  {station.children && station.children.length > 0 && (
                    <div>
                      <strong>Child Stations:</strong> {station.children.length}
                    </div>
                  )}
                </div>
              )}
            </div>

            <SheetFooter className="border-t pt-4 mt-4">
              <Button onClick={handleClose} variant="outline">Close</Button>
            </SheetFooter>
          </>
        ) : (
          <>
            <SheetHeader>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setView("details")}
                  className="h-8 w-8 p-0 -ml-1"
                  title="Back to details"
                >
                  <ArrowLeft className="h-4 w-4" />
                </Button>
                <SheetTitle>System Prompt</SheetTitle>
              </div>
            </SheetHeader>
            <div className="flex-1 overflow-y-auto py-6 space-y-4">
              {isEmpty ? (
                <p className="text-muted-foreground text-sm">
                  No system prompt has been set for this station. Add a system prompt when editing the station to define its behavior and instructions.
                </p>
              ) : (
                <Markdown>{station?.system_prompt || ''}</Markdown>
              )}
            </div>
            <SheetFooter className="border-t pt-4 mt-4">
              <Button onClick={() => setView("details")} variant="outline">Back</Button>
            </SheetFooter>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default ViewStationDialog;
